import type { MagnoliaEvent } from "components/magnolia/Media/MagnoliaEventsApp";
import queryString from "query-string";
import { HTTPClientService } from "services/HTTPClient";
import { getAPIBase } from "../util/MagnoliaAppHelpers";

const basePath = getAPIBase();
const restBaseEventEntries = basePath + process.env.REACT_APP_MGNL_API_EVENTS;

type LoadConfig = {
  filter?: {
    orderBy?: string;
    limit: number;
    offset?: number;
  };
  ancestor?: string;
};

type EventsResponse = {
  total?: number;
  results?: MagnoliaEvent[];
};

export default class EventService {
  readonly httpClientService: HTTPClientService;

  constructor(httpClientService: HTTPClientService) {
    this.httpClientService = httpClientService;
  }

  loadEvents = async (
    { filter, ancestor }: LoadConfig = {
      filter: { orderBy: "from-datetime asc", limit: 100, offset: 0 },
      ancestor: "albis",
    }
  ) => {
    try {
      const httpClient = this.httpClientService.getDefaultHttpClient();

      const pageSize = filter?.limit ?? 100;
      let offset = filter?.offset ?? 0;
      let results: MagnoliaEvent[] = [];
      let total = Infinity;

      // The delivery endpoint clamps each response to its configured maxLimit
      // (default 100, regardless of the requested `limit`), so we page through
      // with `offset` until we've collected `total` results.
      while (offset < total) {
        const { data } = await httpClient.get<EventsResponse>(
          `${restBaseEventEntries}?@ancestor=/${ancestor}&${queryString.stringify(
            { ...filter, limit: pageSize, offset }
          )}&lang=all`
        );

        const pageResults = data?.results ?? [];
        results = results.concat(pageResults);
        total = data?.total ?? results.length;

        // Advance by what the server actually returned, not what we asked
        // for — the endpoint may clamp the page to a smaller configured limit.
        if (pageResults.length === 0) break;
        offset += pageResults.length;
      }

      return { total: results.length, results };
    } catch {
      return { total: 0, results: [] };
    }
  };
}
